import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/formatters';
import { useSelector, useDispatch } from 'react-redux';
import { setQuizCorrectResult, setQuizCorrectResultError, clearQuizCorrectResultError } from '../redux/actions/challengeActions';
import { saveNewParticipation, updateParticipation, deleteParticipation } from '../services/challengeService';
import { getCurrentUserId } from '../utils/userStorage';
import { getQuizTypeByCode, isTuningType } from '../config/quizTypes';
import SingleChoiceQuiz from './quiz/SingleChoiceQuiz';
import TuningQuiz from './quiz/TuningQuiz';
import AttachmentViewer from './AttachmentViewer';
import { MdExpandMore, MdExpandLess, MdCode } from 'react-icons/md';
import { FiFileText, FiInfo, FiAlertTriangle, FiRefreshCw, FiSearch } from 'react-icons/fi';
import axios from 'axios';
import { queryGradingTarget } from '../services/challengeDb';
import { compareQueryResult, FETCH_ROWS } from '../utils/queryGrading';
import { QueryRubricButton } from './quiz/QueryRubricDialog';
import { isLocal, isLocalSync } from '../services/challengeSource';

// axios 인스턴스 생성 (App.js와 동일한 방식)
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/sql-developer',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    },
    timeout: 15000,
    validateStatus: function (status) {
        return status < 500;
    }
});


/**
 * 채점 SQL 을 **어디서 실행할지** — `services/challengeDb` 가 정한다(2026-10-05).
 * 웹은 문제 출처(QBase·가져온 것)와 무관하게 서버의 교육DB(시스템 풀), 스탠드얼론은 활성 연결.
 * 조용히 폴백하지 않는다 — 스탠드얼론인데 연결이 없으면 실행하지 않고 그 사실을 말한다.
 */
const gradingTarget = () => queryGradingTarget();

const ParticipationTab = ({
    selectedChallengeId,
    challenges = [],
    quizList = [],
    allParticipations = [],
    selectedQuizNo = null,
    onSubmissionSuccess = null,
    onShowPlans = null,   // 튜닝 채점 계획을 왼쪽 실행계획 탭으로(App.handleChallengePlans)
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const quizCorrectResultMap = useSelector(state => state.challenge.quizCorrectResultMap || {});
    // 챌린지가 어디서 왔는가 — 채점 SQL 을 어디서 돌릴지가 여기서 갈린다.
    // 누를 때 모듈 캐시를 읽으면 첫 렌더에서 "아직 모름" 일 수 있어 상태로 들고 있는다.
    const [useLocalChallenges, setUseLocalChallenges] = useState(isLocalSync() === true);
    useEffect(() => {
        let alive = true;
        isLocal().then(v => { if (alive) setUseLocalChallenges(v); }).catch(() => {});
        return () => { alive = false; };
    }, []);
    const authUser = useSelector(state => state.auth?.user);
    const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

    // === 모든 상태 선언 ===
    // selectedQuizNo로부터 실제 퀴즈 객체 찾기
    const selectedQuiz = useMemo(() => {
        if (!selectedQuizNo || !quizList || quizList.length === 0) return null;
        return quizList.find(quiz => parseInt(quiz.QUIZ_NO) === selectedQuizNo) || null;
    }, [selectedQuizNo, quizList]);

    // 퀴즈 유형 결정 (기본값: SQL 작성형)
    const quizType = useMemo(() => {
        const quizTypeCode = selectedQuiz?.QUIZ_TCD || '001';
        return getQuizTypeByCode(quizTypeCode);
    }, [selectedQuiz]);
    const [submitQuery, setSubmitQuery] = useState('');
    const [validationMsg, setValidationMsg] = useState('');
    const [submitMsg, setSubmitMsg] = useState('');
    const [isValidated, setIsValidated] = useState(false);
    const [tempResult, setTempResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationStatus, setValidationStatus] = useState(null); // 'error', 'correct', 'wrong'
    const [wrongDetails, setWrongDetails] = useState(''); // 오답인 경우 구체적인 차이점
    const [previousParticipation, setPreviousParticipation] = useState(null); // 현재 선택된 퀴즈의 이전 참여 기록
    const [isQueryExpanded, setIsQueryExpanded] = useState(false); // 이전 참여기록 쿼리 접기/펼치기 상태
    const [isResultExpanded, setIsResultExpanded] = useState(false); // 예상결과 접기/펼치기 상태
    const [quizSelections, setQuizSelections] = useState({}); // 퀴즈별 선택 상태 저장 { quizNo: [selectedIds] }
    const [singleChoiceSubmitMsg, setSingleChoiceSubmitMsg] = useState(''); // 선택형 퀴즈 제출 메시지
    const [quizTimers, setQuizTimers] = useState({}); // 퀴즈별 타이머 상태 저장 { quizKey: remainingTime }

    // 타이머 업데이트 핸들러 (컴포넌트 최상위 레벨에서 선언)
    const handleTimeUpdate = useCallback((remainingTime) => {
        if (!selectedChallengeId || !selectedQuiz?.QUIZ_NO) return;
        const quizKey = `${selectedChallengeId}_${selectedQuiz.QUIZ_NO}`;
        setQuizTimers(prev => ({
            ...prev,
            [quizKey]: remainingTime
        }));
    }, [selectedChallengeId, selectedQuiz?.QUIZ_NO]);

    // selectedQuizNo 변경 시 초기화
    useEffect(() => {
        setSubmitQuery('');
        setValidationMsg('');
        setSubmitMsg('');
        setIsValidated(false);
        setTempResult(null);
        setValidationStatus(null);
        setWrongDetails('');
        setIsQueryExpanded(false);
        setIsResultExpanded(false);
        setSingleChoiceSubmitMsg(''); // 선택형 퀴즈 메시지도 초기화
    }, [selectedQuizNo]);

    // 선택된 퀴즈나 전체 참여 기록이 변경되면 이전 참여 기록 업데이트
    useEffect(() => {
        if (selectedQuiz && allParticipations && allParticipations.length > 0) {
            // 현재 사용자 ID 가져오기
            const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

            const participation = allParticipations.find(p => {
                if (String(p.QUIZ_NO) !== String(selectedQuiz.QUIZ_NO)) return false;
                // 로컬 저장소 — **로그인이 없을 때만** 전부 내 것으로 본다(스탠드얼론, 2026-09-19).
                // 서버가 호출자로 'standalone' 을 저장하는데 화면은 'anonymous' 를 들고 있어 영영 안 맞았다.
                // 그러나 **웹에서 로컬 출처를 쓰면 저장소에 여러 사람의 기록**이 있다 — 거르지 않았더니 남의 제출이
                // 「지난 제출」로 보였고, 초기화(내 것만 지움)가 0건을 지우고 성공으로 끝났다(2026-10-05).
                if (useLocalChallenges) {
                    const anonymous = !currentUserId || currentUserId === 'anonymous';
                    if (anonymous || String(p.USRID) === 'standalone') return true;
                }
                return String(p.USRID) === String(currentUserId);
            }) || null;

            setPreviousParticipation(participation);
        } else {
            setPreviousParticipation(null);
        }
    }, [selectedQuiz, allParticipations, authUser, useLocalChallenges]);

    // 퀴즈 변경 시 쿼리 접기 상태 초기화
    useEffect(() => {
        setIsQueryExpanded(false);
        setIsResultExpanded(false);
    }, [selectedQuiz]);

    // 선택된 퀴즈의 정답 쿼리 결과를 가져와서 Redux에 저장 (SQL 작성형만)
    useEffect(() => {
        const fetchCorrectResult = async () => {
            if (!selectedQuiz || !selectedChallengeId || quizType.code !== '001') {
                return;
            }

            const correctResultKey = `${selectedChallengeId}_${selectedQuiz.QUIZ_NO}`;

            // 이미 저장된 결과가 있는지 확인 (에러 상태 포함 모든 경우 실행 생략)
            if (quizCorrectResultMap && quizCorrectResultMap[correctResultKey]) {
                // 이미 결과가 있으면 (성공이든 에러든) 재실행하지 않음
                return;
            }

            // 정답 쿼리문이 있는지 확인
            if (!selectedQuiz.CRANS_SQL_CN) {
                return;
            }

            try {
                // 정답 쿼리를 실행하여 결과 가져오기
                // 라인 주석(--) 제거 전처리
                const cleanedQuery = selectedQuiz.CRANS_SQL_CN.trim().replace(/--[^\n]*/g, '');

                // 현재 로그인한 사용자 ID 사용 (system 대신)
                const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

                // 웹은 서버의 교육DB, 스탠드얼론은 활성 연결(services/challengeDb)
                const target = gradingTarget();
                if (target.error) throw new Error(t('challenge:grading.noConnection'));

                const requestPayload = {
                    query: cleanedQuery,
                    fetchSize: FETCH_ROWS,   // 행 수 비교가 여기까지 센다(utils/queryGrading)
                    usrid: currentUserId || 'anonymous',
                    ...target,
                };

                const response = await axiosInstance.post('/fetch-data', requestPayload);

                if (response.data && response.data.rows) {
                    const correctResult = response.data.rows;
                    // Redux에 저장
                    dispatch(setQuizCorrectResult(selectedChallengeId, selectedQuiz.QUIZ_NO, correctResult));
                }
            } catch (error) {
                // 오류 상태를 Redux에 저장 (한 번만)
                const errorMessage = error.response?.data?.error ||
                                   error.message ||
                                   t('challenge:pt.err.answerQueryFailed');
                dispatch(setQuizCorrectResultError(selectedChallengeId, selectedQuiz.QUIZ_NO, errorMessage));
            }
        };

        fetchCorrectResult();
    }, [selectedQuiz, selectedChallengeId, dispatch, quizType]); // quizCorrectResultMap 의존성 제거

    // 날짜 문자열을 파싱하는 함수 (YYYYMMDDHH24MISS -> Date)
    const parseCustomDateString = (dateStr) => {
        if (!dateStr || dateStr.length !== 14) {
            return null;
        }

        // YYYYMMDDHH24MISS -> YYYY-MM-DD HH:MM:SS
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const hour = dateStr.substring(8, 10);
        const minute = dateStr.substring(10, 12);
        const second = dateStr.substring(12, 14);

        const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
        const date = new Date(isoString);

        return isNaN(date.getTime()) ? null : date;
    };

    // 제출일시를 포맷팅하는 함수
    const formatSubmissionDate = (dateStr) => {
        if (!dateStr) return t('challenge:pt.noInfo');
        const parsedDate = parseCustomDateString(dateStr);
        return parsedDate ? formatDateTime(parsedDate) : t('challenge:pt.badDate');
    };

    // 제출 쿼리 조회 권한 체크 함수
    const canViewSubmissionQuery = (participation) => {
        if (!participation) return false;

        // 현재 사용자 ID 가져오기
        const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

        // 1. 본인이 제출한 경우
        if (String(participation.USRID) === String(currentUserId)) {
            return true;
        }

        // 2. 담당사용자인 경우 (선택된 챌린지의 MGR_USRID와 비교)
        const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId);
        if (selectedChallenge && selectedChallenge.MGR_USRID &&
            String(selectedChallenge.MGR_USRID) === String(currentUserId)) {
            return true;
        }

        return false;
    };

    // 추가 검증 규칙 파싱 및 검증 함수
    const validateAdditionalRules = (query, rulesJson) => {
        if (!rulesJson) return { isValid: true, errors: [] };

        try {
            const rules = JSON.parse(rulesJson);
            const errors = [];
            const options = rules.options || {};

            // 주석 제거 여부
            let queryToCheck = query;
            if (!options.checkComments) {
                queryToCheck = query.replace(/--[^\n]*/g, '').replace(/\/\*[\s\S]*?\*\//g, '');
            }

            // required 패턴 검증
            if (rules.required && Array.isArray(rules.required)) {
                for (const rule of rules.required) {
                    try {
                        // JavaScript에서 지원하지 않는 인라인 플래그 제거 (예: (?i))
                        const cleanPattern = rule.pattern.replace(/\(\?[imsux]+\)/g, '');
                        const regex = new RegExp(cleanPattern, options.caseSensitive ? '' : 'i');
                        if (!regex.test(queryToCheck)) {
                            errors.push(rule.errorMessage || t('challenge:pt.rule.missingRequired', { rule: rule.description }));
                        }
                    } catch (regexError) {
                        console.error('정규식 오류:', regexError);
                        errors.push(t('challenge:pt.rule.error', { rule: rule.description }));
                    }
                }
            }

            // forbidden 패턴 검증
            if (rules.forbidden && Array.isArray(rules.forbidden)) {
                for (const rule of rules.forbidden) {
                    try {
                        console.log('🔍 [forbidden 검증] 원본 패턴:', rule.pattern);
                        // JavaScript에서 지원하지 않는 인라인 플래그 제거 (예: (?i))
                        const cleanPattern = rule.pattern.replace(/\(\?[imsux]+\)/g, '');
                        console.log('🔍 [forbidden 검증] 정제된 패턴:', cleanPattern);
                        console.log('🔍 [forbidden 검증] 검증할 쿼리:', queryToCheck);
                        const regex = new RegExp(cleanPattern, options.caseSensitive ? '' : 'i');
                        const isFound = regex.test(queryToCheck);
                        console.log('🔍 [forbidden 검증] 패턴 발견됨:', isFound);
                        if (isFound) {
                            errors.push(rule.errorMessage || t('challenge:pt.rule.forbiddenUsed', { rule: rule.description }));
                        }
                    } catch (regexError) {
                        console.error('❌ [forbidden 검증] 정규식 오류:', regexError);
                        console.error('❌ [forbidden 검증] 문제 패턴:', rule.pattern);
                        errors.push(t('challenge:pt.rule.error', { rule: rule.description }));
                    }
                }
            }

            return {
                isValid: errors.length === 0,
                errors: errors
            };
        } catch (parseError) {
            console.error('검증 규칙 파싱 오류:', parseError);
            return { isValid: true, errors: [] }; // 파싱 오류 시 검증 건너뛰기
        }
    };

    // 오답 분석 함수
    const analyzeWrongAnswer = (userResult, correctResult) => {
        const differences = [];

        // 1. 행 개수 비교
        if (userResult.length !== correctResult.length) {
            differences.push(t('challenge:pt.diff.rowCount', { mine: userResult.length, expected: correctResult.length }));
        }

        // 2. 컬럼 비교 (첫 번째 행이 있는 경우)
        if (userResult.length > 0 && correctResult.length > 0) {
            const userColumns = Object.keys(userResult[0]);
            const correctColumns = Object.keys(correctResult[0]);

            if (userColumns.length !== correctColumns.length) {
                differences.push(t('challenge:pt.diff.colCount', { mine: userColumns.length, expected: correctColumns.length }));
            }

            // 컬럼명 비교
            const missingColumns = correctColumns.filter(col => !userColumns.includes(col));
            const extraColumns = userColumns.filter(col => !correctColumns.includes(col));

            if (missingColumns.length > 0) {
                differences.push(t('challenge:pt.diff.missingCols', { cols: missingColumns.join(', ') }));
            }
            if (extraColumns.length > 0) {
                differences.push(t('challenge:pt.diff.extraCols', { cols: extraColumns.join(', ') }));
            }
        }

        // 3. 데이터 내용 비교 (최대 3개 행까지만 상세 분석)
        const maxAnalyzeRows = Math.min(3, Math.min(userResult.length, correctResult.length));
        for (let i = 0; i < maxAnalyzeRows; i++) {
            const userRow = userResult[i];
            const correctRow = correctResult[i];

            if (userRow && correctRow) {
                const commonColumns = Object.keys(correctRow).filter(col => Object.keys(userRow).includes(col));

                for (const col of commonColumns) {
                    if (String(userRow[col]) !== String(correctRow[col])) {
                        differences.push(t('challenge:pt.diff.cell', { row: i + 1, col, mine: userRow[col], expected: correctRow[col] }));

                        // 최대 5개 차이점까지만 표시
                        if (differences.length >= 5) {
                            differences.push(t('challenge:pt.moreDiffs'));
                            return differences;
                        }
                    }
                }
            }
        }

        return differences;
    };

    // 사전검증 - SQLEditor와 동일한 방식으로 쿼리 실행
    const handleValidate = async () => {
        if (!submitQuery.trim()) {
            setValidationMsg(t('challenge:pt.err.queryRequired'));
            return;
        }

        setIsValidating(true);
        setValidationMsg('');
        setIsValidated(false);
        setTempResult(null);
        setValidationStatus(null);
        setWrongDetails('');

        try {
            // 1단계: 추가 검증 규칙 체크 (ADD_CRANS_VERIF_CN)
            console.log('=== 추가 정답 검증 내용 ===');
            console.log('selectedQuiz.ADD_CRANS_VERIF_CN:', selectedQuiz?.ADD_CRANS_VERIF_CN);

            if (selectedQuiz && selectedQuiz.ADD_CRANS_VERIF_CN) {
                console.log('추가 검증 규칙 적용 시작');
                const ruleValidation = validateAdditionalRules(submitQuery.trim(), selectedQuiz.ADD_CRANS_VERIF_CN);
                console.log('검증 결과:', ruleValidation);

                if (!ruleValidation.isValid) {
                    setValidationStatus('wrong');
                    setValidationMsg(t('challenge:pt.msg.ruleViolation'));
                    setWrongDetails(ruleValidation.errors.join(' | '));
                    setIsValidated(true);
                    return;
                }
            } else {
                console.log('추가 검증 규칙 없음 (ADD_CRANS_VERIF_CN이 비어있거나 null)');
            }

            // 2단계: 쿼리 실행 검증
            // 라인 주석(--) 제거 전처리
            const cleanedQuery = submitQuery.trim().replace(/--[^\n]*/g, '');

            // SQLEditor와 동일한 /fetch-data 엔드포인트 사용
            // 현재 로그인한 사용자 ID 사용 (system 대신)
            const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

            // 정답 SQL 과 **같은 곳에서** 돌려야 결과 비교가 뜻을 갖는다
            const submitTarget = gradingTarget();
            if (submitTarget.error) throw new Error(t('challenge:grading.noConnection'));

            const response = await axiosInstance.post('/fetch-data', {
                query: cleanedQuery,
                fetchSize: FETCH_ROWS, // 정답 SQL 과 같은 수 — 행 수 비교(utils/queryGrading)
                usrid: currentUserId || 'anonymous', // 현재 사용자로 실행 (검증용)
                ...submitTarget,
            });

            // 성공적으로 실행됨
            const { rows, totalExecutionTime, currentFetchTime, message: responseMessage } = response.data;
            setTempResult(rows || []);
            setIsValidated(true);

            const executionTimeMsg = totalExecutionTime
                ? t('challenge:pt.time.total', { ms: totalExecutionTime })
                : currentFetchTime
                ? t('challenge:pt.time.exec', { ms: currentFetchTime })
                : '';

            // 3단계: 정답과 비교하여 상태 결정
            const correctResultKey = `${selectedChallengeId}_${selectedQuiz?.QUIZ_NO}`;
            const correctResult = quizCorrectResultMap && quizCorrectResultMap[correctResultKey];

            if (selectedQuiz && correctResult) {
                // 정답 쿼리 오류 상태 체크
                if (correctResult.error) {
                    setValidationStatus('error');
                    setValidationMsg(t('challenge:pt.err.cannotCompare', { message: correctResult.error }));
                    return;
                }

                try {
                    // 비교 규칙은 utils/queryGrading 한 곳 — 「평가 기준」 창이 같은 상수를 보여 준다
                    const cmp = compareQueryResult(rows, correctResult);
                    const normalizedUserResult = cmp.user;
                    const normalizedCorrectResult = cmp.answer;

                    if (cmp.correct) {
                        setValidationStatus('correct');
                        setValidationMsg(t('challenge:pt.msg.correctWithTime', { time: executionTimeMsg }));
                        setWrongDetails('');
                    } else {
                        setValidationStatus('wrong');

                        // 오답 상세 분석
                        const differences = cmp.sameTop
                            // 처음 10행은 같은데 행 수가 다르다 — 11행째부터 더 내거나 덜 냈다
                            ? [t('challenge:pt.diff.rowCountBeyond', { mine: cmp.userCount, expected: cmp.answerCount, max: FETCH_ROWS })]
                            : analyzeWrongAnswer(normalizedUserResult, normalizedCorrectResult);
                        const detailsMsg = differences.length > 0 ? differences.join(' | ') : t('challenge:pt.diff.mismatch');

                        setValidationMsg(t('challenge:pt.msg.wrongWithTime', { time: executionTimeMsg }));
                        setWrongDetails(detailsMsg);
                    }
                } catch (parseError) {
                    console.error('정답 데이터 파싱 오류:', parseError);
                    setValidationStatus('wrong');
                    setValidationMsg(t('challenge:pt.msg.validatedNoCompare', { time: executionTimeMsg }));
                }
            } else {
                setValidationStatus('wrong');
                setValidationMsg(t('challenge:pt.msg.validatedLoading', { time: executionTimeMsg }));
            }
        } catch (error) {
            console.error('쿼리 검증 실패:', error);
            const errorMessage = error.response?.data?.error ||
                               (error.response?.data ? JSON.stringify(error.response.data) : null) ||
                               error.message ||
                               t('challenge:pt.err.queryFailed');
            setValidationMsg(t('challenge:pt.err.queryMsg', { message: errorMessage }));
            setIsValidated(true); // 오류도 검증 완료로 간주
            setTempResult(null);
            setValidationStatus('error');
        } finally {
            setIsValidating(false);
        }
    };

    // 제출 버튼 레이블 계산
    const getSubmitButtonLabel = () => {
        if (isSubmitting) return t('challenge:pt.submitting');
        if (!isValidated) return t('challenge:pt.submit');

        switch (validationStatus) {
            case 'error':
                return t('challenge:pt.submitError');
            case 'correct':
                return t('challenge:pt.submitCorrect');
            case 'wrong':
                return t('challenge:pt.submitWrong');
            default:
                return t('challenge:pt.submit');
        }
    };

    // 제출 - 모든 검증 상태에서 제출 가능
    const handleSubmit = async () => {
        if (!isValidated) {
            setSubmitMsg(t('challenge:pt.validateFirst'));
            return;
        }

        setIsSubmitting(true);
        setSubmitMsg('');

        try {
            // 검증 상태에 따른 정답 여부 결정
            let isCorrect = false;

            // 신규/수정 구분
            const submitTypeMsg = previousParticipation ? t('challenge:pt.submitEdit') : t('challenge:pt.submitNew');

            switch (validationStatus) {
                case 'correct':
                    isCorrect = true;
                    setSubmitMsg(t('challenge:pt.msg.correctSubmitting', { submitType: submitTypeMsg }));
                    break;
                case 'wrong':
                    isCorrect = false;
                    setSubmitMsg(t('challenge:pt.msg.wrongSubmitting', { submitType: submitTypeMsg }));
                    break;
                case 'error':
                    isCorrect = false;
                    setSubmitMsg(t('challenge:pt.msg.submittingError', { submitType: submitTypeMsg }));
                    break;
                default:
                    isCorrect = false;
                    setSubmitMsg(t('challenge:pt.msg.submittingResult', { submitType: submitTypeMsg }));
                    break;
            }

            // 서버에 제출 결과 저장
            try {
                // 현재 로그인된 사용자 ID 가져오기
                const userIdFromStorage = getCurrentUserId();
                const userId = authUser?.usrid || authUser?.id || authUser?.username || userIdFromStorage;
                const quizNo = selectedQuiz.QUIZ_NO;

                // 현재 선택된 참여그룹에서 groupId 가져오기
                let groupId = null;
                const savedGroup = localStorage.getItem('selectedParticipationGroup');
                if (savedGroup) {
                    try {
                        const parsedGroup = JSON.parse(savedGroup);
                        groupId = parsedGroup.qryChallengePtcpGrpId;
                    } catch (error) {
                        console.error('❌ localStorage 참여그룹 파싱 오류:', error);
                    }
                }

                console.log('🔍 [제출 디버깅] 전송할 데이터:', {
                    selectedChallengeId,
                    quizNo,
                    userId,
                    groupId,
                    userIdFromStorage,
                    authUser,
                    submitQuery: submitQuery.trim(),
                    isCorrect
                });

                // 로그인되지 않은 경우 처리.
                // **로컬(가져온) 챌린지는 예외다** — 스탠드얼론에는 로그인이 없고, 서버가
                // 제출자를 호출자로 정한다. 여기서 막으면 내부망에서는 풀어도 제출이 안 된다
                // (실측 2026-09-19: 「로그인이 필요합니다」 로 채점 자체가 막혔다).
                if (!useLocalChallenges && (!userId || userId === 'anonymous')) {
                    throw new Error(t('challenge:pt.err.needLogin'));
                }

                // 챌린지 공개여부 확인
                const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId);
                const publicYn = selectedChallenge?.PUBLIC_YN || 'Y';

                // 비공개 챌린지인 경우에만 groupId 필수
                if (publicYn === 'N' && !groupId) {
                    throw new Error(t('challenge:pt.err.groupRequired'));
                }

                // 기존 참여 데이터가 있는지 확인하여 신규/수정 결정
                let saveResult;

                if (previousParticipation) {
                    // 기존 제출 이력이 있는 경우 바로 수정으로 저장
                    saveResult = await updateParticipation(
                        selectedChallengeId,
                        quizNo,
                        userId,
                        groupId,
                        submitQuery.trim(),
                        isCorrect,
                        isCorrect ? 100 : 0,
                        publicYn
                    );
                } else {
                    // 기존 이력이 없는 경우 신규로 저장
                    saveResult = await saveNewParticipation(
                        selectedChallengeId,
                        quizNo,
                        userId,
                        groupId,
                        submitQuery.trim(),
                        isCorrect,
                        isCorrect ? 100 : 0,
                        publicYn
                    );
                }


                // 성공 메시지 업데이트 (신규/수정 구분)
                const submitType = previousParticipation ? t('challenge:pt.submitEdit') : t('challenge:pt.submitNew');
                if (isCorrect) {
                    setSubmitMsg(t('challenge:pt.msg.correctDone', { submitType: submitType }));
                } else {
                    setSubmitMsg(t('challenge:pt.msg.wrongDone', { submitType: submitType }));
                }

                // 제출 성공 후 퀴즈 버튼 상태 업데이트를 위한 새로고침
                if (onSubmissionSuccess) {
                    console.log('제출 성공 후 새로고침 실행');
                    onSubmissionSuccess();
                }

            } catch (saveError) {
                console.error('서버 저장 실패:', saveError);

                // 저장 실패 시에도 결과는 알려주되, 저장 실패 메시지 추가
                const resultMsg = isCorrect ? t('challenge:pt.msg.correct') : t('challenge:pt.msg.wrongRetry');
                setSubmitMsg(t('challenge:pt.err.saveFailed', { result: resultMsg, message: saveError.message }));
            }
        } catch (error) {
            console.error('제출 처리 실패:', error);
            setSubmitMsg(t('challenge:pt.err.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // 퀴즈가 선택되지 않았을 때
    if (!selectedQuizNo || !selectedQuiz) {
        return (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                <div style={{ marginBottom: 12, fontSize: 48, opacity: 0.5, display: 'flex', justifyContent: 'center' }}>
                    <FiFileText size={48} />
                </div>
                <div>{t('challenge:pt.pickQuizAbove')}</div>
            </div>
        );
    }

    // 단일선택형 퀴즈인 경우 별도 컴포넌트 렌더링
    // ── 튜닝형(003·004) ───────────────────────────────────────────────────
    //
    // **여기에 분기가 없으면 003·004 는 조용히 001 UI 로 떨어진다** — 001 이 조건 분기가
    // 아니라 나머지 전부를 받는 fall-through 이기 때문이다.
    //
    // 채점은 서버가 한다(실측이 필요하다). 화면은 답을 받아 보내고 결과를 보여 줄 뿐이다.
    // 저장은 001·002 와 **같은 함수**를 쓴다 — 경로가 갈리면 한쪽만 고쳐진다.
    if (isTuningType(quizType.code)) {
        const handleGraded = async (graded) => {
            setIsSubmitting(true);
            try {
                const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();
                // 로컬 챌린지는 로그인 없이 풀고 채점한다 (001·002 와 같은 규칙)
                if (!useLocalChallenges && (!currentUserId || currentUserId === 'anonymous')) {
                    throw new Error(t('challenge:pt.err.needLogin'));
                }

                // 최종 점수는 재심이 있으면 그쪽이다. **기계 점수도 함께 남긴다** —
                // 나중에 "왜 이 점수인가" 를 설명할 수 있어야 한다.
                const machineScore = graded.score;
                const finalScore = graded.review && !graded.review.invalid ? graded.review.score : machineScore;
                const isCorrect = finalScore >= 10;

                // 수치는 사유 칸에 JSON 으로 싣는다(사용자 결정 2026-09-19). 사유 칸의 본래
                // 뜻과 섞이지 않게 `tuning` 으로 감싼다. 로컬 저장소에는 점수 컬럼이 없다 —
                // 점수로 집계할 일이 생기면 ensureColumns 로 컬럼을 올려야 한다.
                const detail = JSON.stringify({
                    tuning: {
                        score: finalScore,
                        machineScore,
                        reviewScore: graded.review && !graded.review.invalid ? graded.review.score : null,
                        outcome: graded.outcome,
                        answerBuffers: graded.metrics?.answer?.buffers ?? null,
                        submittedBuffers: graded.metrics?.submitted?.buffers ?? null,
                        ratio: graded.ratio ?? null,
                    },
                }).slice(0, 3900);

                const groupId = (() => {
                    try {
                        const g = JSON.parse(localStorage.getItem('selectedParticipationGroup') || 'null');
                        return g?.GROUP_ID || g?.groupId || null;
                    } catch (_e) { return null; }
                })();
                const publicYn = selectedQuiz?.PUBLIC_YN || 'Y';

                const args = [
                    selectedChallengeId, selectedQuiz.QUIZ_NO, currentUserId, groupId,
                    graded.submitted || '', isCorrect, finalScore * 10, publicYn, detail,
                ];
                if (previousParticipation) await updateParticipation(...args);
                else await saveNewParticipation(...args);

                if (onSubmissionSuccess) onSubmissionSuccess();
            } catch (error) {
                console.error('튜닝 채점 결과 저장 실패:', error);
                // 저장이 실패해도 **점수는 이미 화면에 있다** — 그것까지 지우지 않는다.
                // 튜닝 화면은 TuningQuiz 가 그려 이 탭의 submitMsg 가 보이지 않는다 — 던져서 그쪽 오류 칸에 띄운다
                throw new Error(t('challenge:pt.err.submitFailedMsg', { message: error.message }));
            } finally {
                setIsSubmitting(false);
            }
        };

        // 초기화 — 내 제출 기록을 지워 처음부터 다시 풀게 한다(2026-10-05). 저장과 같은 공급원 갈림길(livesqlRepo)을 지난다
        const handleReset = async () => {
            setIsSubmitting(true);
            try {
                const groupId = (() => {
                    try {
                        const g = JSON.parse(localStorage.getItem('selectedParticipationGroup') || 'null');
                        return g?.GROUP_ID || g?.groupId || null;
                    } catch (_e) { return null; }
                })();
                const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();
                const r = await deleteParticipation(selectedChallengeId, selectedQuiz.QUIZ_NO, currentUserId, groupId, selectedQuiz?.PUBLIC_YN || 'Y');
                if (onSubmissionSuccess) onSubmissionSuccess();   // 참여 기록을 다시 읽는다
                // 로컬은 지운 건수를 돌려준다 — 0건이면 성공처럼 끝내지 않고 말한다(남의 기록이 보이던 때 그랬다)
                if (r && r.deleted === 0) throw new Error(t('challenge:tuning.resetNothing'));
            } catch (error) {
                // 튜닝 화면은 TuningQuiz 가 통째로 그려 이 탭의 submitMsg 가 보이지 않는다 — 던져서 그쪽 오류 칸에 띄운다
                console.error('튜닝 제출 초기화 실패:', error);
                throw error;
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <TuningQuiz
                quiz={selectedQuiz}
                challengeId={selectedChallengeId}
                participation={previousParticipation}
                onGraded={handleGraded}
                onReset={handleReset}
                onShowPlans={onShowPlans}
                isSaving={isSubmitting}
            />
        );
    }

    if (quizType.code === '002') {
        const handleSingleChoiceSubmit = async (selectedChoice) => {
            setIsSubmitting(true);
            console.log('🚀 선택형 퀴즈 제출 시작:', { selectedChoice, previousParticipation });

            try {
                const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

                // 단일선택형도 같다 — 로컬 챌린지는 로그인 없이 풀고 채점한다
                if (!useLocalChallenges && (!currentUserId || currentUserId === 'anonymous')) {
                    throw new Error(t('challenge:pt.err.needLogin'));
                }

                // 정답 확인 (CRANS_RST_CN에서 정답 추출)
                let isCorrect = false;
                try {
                    const resultData = JSON.parse(selectedQuiz.CRANS_RST_CN || '{}');
                    const correctAnswers = (resultData.choices || [])
                        .filter(choice => choice.isAnswer === 'Y')
                        .map(choice => choice.id);

                    // 선택한 답안을 배열로 변환
                    const selectedArray = selectedChoice.split(',').filter(c => c).sort();
                    const correctArray = correctAnswers.sort();

                    // 배열 비교 (순서 무관)
                    isCorrect = JSON.stringify(selectedArray) === JSON.stringify(correctArray);

                    console.log('✅ 정답 확인:', {
                        선택한답안: selectedArray,
                        정답: correctArray,
                        결과: isCorrect
                    });
                } catch (error) {
                    console.error('❌ 정답 확인 중 오류:', error);
                    isCorrect = false;
                }

                // 현재 선택된 참여그룹에서 groupId 가져오기
                let groupId = null;
                const savedGroup = localStorage.getItem('selectedParticipationGroup');
                if (savedGroup) {
                    try {
                        const parsedGroup = JSON.parse(savedGroup);
                        groupId = parsedGroup.qryChallengePtcpGrpId;
                    } catch (error) {
                        console.error('❌ localStorage 참여그룹 파싱 오류:', error);
                    }
                }

                // 챌린지 공개여부 확인
                const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId);
                const publicYn = selectedChallenge?.PUBLIC_YN || 'Y';

                // 비공개 챌린지인 경우에만 groupId 필수
                if (publicYn === 'N' && !groupId) {
                    console.error('❌ 비공개 챌린지는 groupId 필수:', {
                        localStorage참여그룹: localStorage.getItem('selectedParticipationGroup'),
                        savedGroup,
                        선택된챌린지ID: selectedChallengeId,
                        공개여부: publicYn
                    });
                    throw new Error(t('challenge:pt.err.groupRequired'));
                }

                // 서버에 제출 결과 저장
                console.log('💾 서버 저장 시작:', {
                    저장구분: previousParticipation ? '수정' : '신규',
                    challengeId: selectedChallengeId,
                    quizNo: selectedQuiz.QUIZ_NO,
                    userId: currentUserId,
                    groupId,
                    publicYn,
                    selectedChoice,
                    isCorrect,
                    score: isCorrect ? 100 : 0
                });

                if (previousParticipation) {
                    const result = await updateParticipation(
                        selectedChallengeId,
                        selectedQuiz.QUIZ_NO,
                        currentUserId,
                        groupId,
                        selectedChoice,
                        isCorrect,
                        isCorrect ? 100 : 0,
                        publicYn
                    );
                    console.log('✅ 수정 저장 완료:', result);
                } else {
                    const result = await saveNewParticipation(
                        selectedChallengeId,
                        selectedQuiz.QUIZ_NO,
                        currentUserId,
                        groupId,
                        selectedChoice,
                        isCorrect,
                        isCorrect ? 100 : 0,
                        publicYn
                    );
                    console.log('✅ 신규 저장 완료:', result);
                }

                // 제출 성공 메시지 (alert 대신 state에 저장)
                const submitType = previousParticipation ? t('challenge:pt.submitEdit') : t('challenge:pt.submitNew');
                if (isCorrect) {
                    setSingleChoiceSubmitMsg(t('challenge:pt.msg.correctDone', { submitType: submitType }));
                } else {
                    setSingleChoiceSubmitMsg(t('challenge:pt.msg.wrongDone', { submitType: submitType }));
                }

                // 제출 성공 후 새로고침
                if (onSubmissionSuccess) {
                    console.log('🔄 제출 성공 후 새로고침 실행');
                    onSubmissionSuccess();
                }
            } catch (error) {
                console.error('❌ 제출 처리 실패:', error);
                setSingleChoiceSubmitMsg(t('challenge:pt.err.submitFailedMsg', { message: error.message }));
            } finally {
                setIsSubmitting(false);
                console.log('🏁 제출 처리 종료');
            }
        };

        // 선택 변경 핸들러
        const handleSelectionChange = (selectedChoices) => {
            // 챌린지ID + 퀴즈번호로 키 생성
            const quizKey = `${selectedChallengeId}_${selectedQuiz.QUIZ_NO}`;
            setQuizSelections(prev => ({
                ...prev,
                [quizKey]: selectedChoices
            }));
        };

        // 현재 퀴즈의 저장된 선택 가져오기
        const quizKey = `${selectedChallengeId}_${selectedQuiz.QUIZ_NO}`;
        const currentSelection = quizSelections[quizKey] || [];
        const currentTimeLeft = quizTimers[quizKey]; // 저장된 타이머 시간 (undefined면 초기화)

        return (
            <SingleChoiceQuiz
                quiz={selectedQuiz}
                onSubmit={handleSingleChoiceSubmit}
                participation={previousParticipation}
                isSubmitting={isSubmitting}
                initialSelection={currentSelection}
                onSelectionChange={handleSelectionChange}
                challengeId={selectedChallengeId}
                submitMessage={singleChoiceSubmitMsg}
                initialTimeLeft={currentTimeLeft}
                onTimeUpdate={handleTimeUpdate}
            />
        );
    }

    // SQL 작성형 퀴즈 (기존 UI 유지)
    return (
        <div style={{ padding: 8, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {selectedQuiz && (
                <>
                    {/* 퀴즈 내용과 힌트를 나란히 배치 */}
                    <div style={{ marginBottom: 8, flex: '1 1 auto', minHeight: 100, maxHeight: '45%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {selectedQuiz.QUIZ_HINT ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
                                {/* 문제 내용 */}
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontWeight: 'bold', color: 'var(--color-text)', fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiFileText size={14} />{t('challenge:pt.quizLabel', { no: selectedQuiz.QUIZ_NO })}</span>
                                        <AttachmentViewer
                                            attachmentList={selectedQuiz.ATCH_FILE_LST_CN}
                                            challengeId={selectedChallengeId}
                                            quizNo={selectedQuiz.QUIZ_NO}
                                        />
                                        {/* 평가 기준 — 무엇으로 정답인지 풀기 전에 볼 수 있어야 한다(2026-10-05) */}
                                        <QueryRubricButton kind="001" style={{ marginLeft: 'auto' }} />
                                    </div>
                                    <textarea
                                        value={selectedQuiz.QUIZ_DC || ''}
                                        readOnly
                                        style={{
                                            width: '100%',
                                            flex: 1,
                                            minHeight: 70,
                                            resize: 'none',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border-light)',
                                            borderRadius: 4,
                                            fontWeight: 'bold',
                                            fontSize: 12,
                                            padding: 8,
                                            boxSizing: 'border-box',
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none',
                                            MozUserSelect: 'none',
                                            msUserSelect: 'none'
                                        }}
                                    />
                                </div>

                                {/* 퀴즈 힌트 */}
                                <div style={{ width: 339.97, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontWeight: 'bold', color: 'var(--color-quiz-warn-text)', fontSize: 13, marginBottom: 4 }}>
                                        <FiInfo size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('challenge:pt.hint')}
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        minHeight: 70,
                                        padding: 8,
                                        background: 'var(--color-quiz-warn-bg)',
                                        border: '1px solid var(--color-quiz-warn-border)',
                                        borderRadius: 4,
                                        fontSize: 11,
                                        boxSizing: 'border-box',
                                        overflow: 'auto'
                                    }}>
                                        <div style={{ color: 'var(--color-quiz-warn-text)', lineHeight: 1.4 }}>
                                            {selectedQuiz.QUIZ_HINT}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                <div style={{ fontWeight: 'bold', color: 'var(--color-text)', fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiFileText size={14} />{t('challenge:pt.quizLabel', { no: selectedQuiz.QUIZ_NO })}</span>
                                    <AttachmentViewer
                                        attachmentList={selectedQuiz.ATCH_FILE_LST_CN}
                                        challengeId={selectedChallengeId}
                                        quizNo={selectedQuiz.QUIZ_NO}
                                    />
                                    <QueryRubricButton kind="001" style={{ marginLeft: 'auto' }} />
                                </div>
                                <textarea
                                    value={selectedQuiz.QUIZ_DC || ''}
                                    readOnly
                                    style={{
                                        width: '100%',
                                        flex: 1,
                                        minHeight: 70,
                                        resize: 'none',
                                        background: 'var(--color-bg-secondary)',
                                        border: '1px solid var(--color-border-light)',
                                        borderRadius: 4,
                                        fontWeight: 'bold',
                                        fontSize: 12,
                                        padding: 8,
                                        boxSizing: 'border-box',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                        MozUserSelect: 'none',
                                        msUserSelect: 'none'
                                    }}
                                />
                            </div>
                        )}

                        {/* 이전 참여 기록 */}
                        {previousParticipation && (
                            <div style={{
                                marginTop: 6,
                                padding: 6,
                                flexShrink: 0,
                                background: 'var(--color-quiz-success-bg)',
                                border: '1px solid var(--color-quiz-ok-border)',
                                borderRadius: 4,
                                fontSize: 11
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 6
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 'bold', color: 'var(--color-quiz-success-text)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <FiFileText size={13} />{t('challenge:pt.previousRecord')}
                                        </span>
                                        <span style={{
                                            fontWeight: 'normal',
                                            color: previousParticipation.SUCCSS_YN === 'Y' ? 'var(--color-quiz-success-text)' : 'var(--color-quiz-fail-text)',
                                            marginLeft: 8
                                        }}>
                                            {previousParticipation.SUCCSS_YN === 'Y' ? t('challenge:pt.badge.correct') : t('challenge:pt.badge.wrong')}
                                        </span>
                                        {previousParticipation.SBMSN_SQL_CN && canViewSubmissionQuery(previousParticipation) && (
                                            <button
                                                onClick={() => setIsQueryExpanded(!isQueryExpanded)}
                                                style={{
                                                    background: 'transparent',
                                                    color: 'var(--color-text-secondary)',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    verticalAlign: 'middle',
                                                    marginLeft: 8
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text)'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                                                title={isQueryExpanded ? t('challenge:pt.hideQuery') : t('challenge:pt.showQuery')}
                                            >
                                                {isQueryExpanded ? <MdExpandLess size={16} /> : <MdCode size={16} />}
                                                <span style={{ fontSize: 11 }}>{isQueryExpanded ? t('challenge:pt.hide') : t('challenge:pt.query')}</span>
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>
                                            {formatSubmissionDate(previousParticipation.SBMSN_DT)}
                                        </span>
                                        {previousParticipation.SBMSN_SQL_CN && canViewSubmissionQuery(previousParticipation) && (
                                            <button
                                                onClick={() => {
                                                    setSubmitQuery(previousParticipation.SBMSN_SQL_CN);
                                                    // 복사 시 검증 상태 초기화
                                                    setIsValidated(false);
                                                    setTempResult(null);
                                                    setValidationMsg('');
                                                    setSubmitMsg('');
                                                    setValidationStatus(null);
                                                    setWrongDetails('');
                                                }}
                                                style={{
                                                    fontSize: 9,
                                                    padding: '2px 6px',
                                                    background: 'var(--color-bg)',
                                                    color: 'var(--color-text)',
                                                    border: '1px solid var(--color-border)',
                                                    borderRadius: 3,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('challenge:pt.copyToInput')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {previousParticipation.SBMSN_SQL_CN && isQueryExpanded && canViewSubmissionQuery(previousParticipation) && (
                                    <div style={{ marginTop: 4 }}>
                                        <textarea
                                            value={previousParticipation.SBMSN_SQL_CN}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                height: '80px',
                                                resize: 'vertical',
                                                background: 'var(--color-bg-secondary)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 3,
                                                fontSize: 10,
                                                padding: 4,
                                                fontFamily: 'monospace',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 예상 결과 그리드 */}
                    <div style={{ marginBottom: 8, flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <h5 style={{ margin: 0, color: 'var(--color-text)', fontSize: 13 }}>
                                {tempResult ? t('challenge:pt.validationResult') : t('challenge:pt.expectedResult')}
                            </h5>
                            <button
                                onClick={() => setIsResultExpanded(!isResultExpanded)}
                                style={{
                                    background: 'transparent',
                                    color: 'var(--color-text-secondary)',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    verticalAlign: 'middle'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                            >
                                {isResultExpanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                                <span style={{ fontSize: 11 }}>{isResultExpanded ? t('challenge:pt.hide') : t('challenge:pt.show')}</span>
                            </button>
                        </div>
                        {isResultExpanded && (
                            <div style={{ border: '1px solid var(--color-border-light)', borderRadius: 4, background: 'var(--color-bg-secondary)', height: 100, padding: 6, overflow: 'auto' }}>
                            {tempResult ? (
                                // 사전검증 후 임시 결과 표시
                                <div style={{ fontSize: 11 }}>
                                    {tempResult.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                            <thead>
                                                <tr style={{ background: 'var(--color-grid-header)' }}>
                                                    {Object.keys(tempResult[0] || {}).map((key, idx) => (
                                                        <th key={idx} style={{ border: '1px solid var(--color-border)', padding: 4, textAlign: 'left' }}>
                                                            {key}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tempResult.slice(0, 10).map((row, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        {Object.values(row).map((value, colIdx) => (
                                                            <td key={colIdx} style={{ border: '1px solid var(--color-border)', padding: 4 }}>
                                                                {String(value)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <span style={{ color: 'var(--color-text-secondary)' }}>{t('challenge:pt.noResult')}</span>
                                    )}
                                </div>
                            ) : selectedQuiz && selectedQuiz.CRANS_SQL_CN ? (
                                // 정답 결과 표시
                                (() => {
                                    try {
                                        const correctResultKey = `${selectedChallengeId}_${selectedQuiz?.QUIZ_NO}`;
                                        const correctResult = quizCorrectResultMap && quizCorrectResultMap[correctResultKey];

                                        if (!correctResult) {
                                            return <span style={{ color: 'var(--color-text-secondary)' }}>{t('challenge:pt.loadingAnswer')}</span>;
                                        }

                                        // 오류 상태 체크
                                        if (correctResult.error) {
                                            return (
                                                <div style={{ color: 'var(--color-quiz-fail-text)', padding: '10px', backgroundColor: 'var(--color-quiz-fail-bg)', border: '1px solid var(--color-quiz-fail-border)', borderRadius: '4px' }}>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            <FiAlertTriangle size={13} />{t('challenge:pt.err.answerQueryPrefix')}
                                                        </strong><br />
                                                        {correctResult.error}
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            // 에러 상태를 클리어하고 다시 실행하도록 트리거
                                                            dispatch(clearQuizCorrectResultError(selectedChallengeId, selectedQuiz.QUIZ_NO));
                                                        }}
                                                        style={{
                                                            fontSize: 11,
                                                            padding: '4px 8px',
                                                            background: 'var(--color-bg)',
                                                            color: 'var(--color-text)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: 3,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <FiRefreshCw size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('challenge:pt.retry')}
                                                    </button>
                                                </div>
                                            );
                                        }

                                        const displayResult = correctResult.slice(0, 10);

                                        if (displayResult.length > 0) {
                                            return (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                    <thead>
                                                        <tr style={{ background: 'var(--color-grid-header)' }}>
                                                            {Object.keys(displayResult[0] || {}).map((key, idx) => (
                                                                <th key={idx} style={{ border: '1px solid var(--color-border)', padding: 4, textAlign: 'left' }}>
                                                                    {key}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayResult.map((row, rowIdx) => (
                                                            <tr key={rowIdx}>
                                                                {Object.values(row).map((value, colIdx) => (
                                                                    <td key={colIdx} style={{ border: '1px solid var(--color-border)', padding: 4 }}>
                                                                        {String(value)}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            );
                                        } else {
                                            return <span style={{ color: 'var(--color-text-secondary)' }}>{t('challenge:pt.noAnswerResult')}</span>;
                                        }
                                    } catch (error) {
                                        return <span style={{ color: 'var(--color-quiz-fail-text)' }}>{t('challenge:pt.answerParseError')}</span>;
                                    }
                                })()
                            ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>{t('challenge:pt.pickQuizForExpected')}</span>
                            )}
                            </div>
                        )}
                    </div>

                    {/* 답안 입력 및 버튼 영역 - 가로 배치 */}
                    <div style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0 }}>
                        {/* 쿼리 입력 영역 */}
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            <textarea
                                value={submitQuery}
                                onChange={(e) => {
                                    setSubmitQuery(e.target.value);
                                    // 쿼리 내용이 변경되면 검증 상태 초기화
                                    if (isValidated) {
                                        setIsValidated(false);
                                        setTempResult(null);
                                        setValidationMsg('');
                                        setSubmitMsg('');
                                        setValidationStatus(null);
                                        setWrongDetails('');
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '120px',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 4,
                                    padding: 8,
                                    fontSize: 13,
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    resize: 'vertical',
                                    background: 'var(--color-bg)',
                                    boxSizing: 'border-box'
                                }}
                                placeholder={t('challenge:pt.queryPlaceholder')}
                            />
                        </div>

                        {/* 버튼 및 메시지 영역 - 우측 고정 너비 */}
                        <div style={{ width: 339.97, flexShrink: 0, padding: 8, background: 'var(--color-bg-secondary)', borderRadius: 6, fontSize: 12, display: 'flex', flexDirection: 'column', minHeight: 0, boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}>
                                {/* 버튼들 - 좌우 배치 */}
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {/* 사전검증 버튼 */}
                                    <button
                                        onClick={handleValidate}
                                        disabled={isValidating || !submitQuery.trim()}
                                        style={{
                                            flex: 1,
                                            padding: '8px 12px',
                                            // 보조 동작이다 — 회색 계열로 두고 강조는 제출에만 준다 (그라데이션 제거 2026-09-19)
                                            background: 'var(--color-bg-secondary)',
                                            color: isValidating ? 'var(--color-text-muted)' : 'var(--color-text)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 4,
                                            cursor: isValidating || !submitQuery.trim() ? 'not-allowed' : 'pointer',
                                            fontSize: 11,
                                            fontWeight: '600',
                                            transition: 'background 150ms ease, color 150ms ease, border-color 150ms ease'
                                        }}
                                    >
                                        {isValidating ? t('challenge:pt.validating') : (
                                            <><FiSearch size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('challenge:pt.validate')}</>
                                        )}
                                    </button>

                                    {/* 제출 버튼 */}
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!isValidated || isSubmitting}
                                        style={{
                                            flex: 1,
                                            padding: '8px 12px',
                                            // 주 동작 — 채움색 하나로. 그라데이션·그림자를 쓰지 않는다
                                            background: isSubmitting ? 'var(--color-text-muted)' : 'var(--color-accent)',
                                            color: 'var(--color-bg)',
                                            border: 'none',
                                            borderRadius: 4,
                                            cursor: isSubmitting || !isValidated ? 'not-allowed' : 'pointer',
                                            fontSize: 11,
                                            fontWeight: '600',
                                            transition: 'background 150ms ease, color 150ms ease, border-color 150ms ease'
                                        }}
                                    >
                                        {isSubmitting ? t('challenge:pt.submitting2') : (validationStatus === 'correct' ? t('challenge:pt.submitCorrectShort') : validationStatus === 'wrong' ? t('challenge:pt.submitWrongShort') : validationStatus === 'error' ? t('challenge:pt.submitErrorShort') : t('challenge:pt.submit'))}
                                    </button>
                                </div>

                                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.4, marginTop: 8 }}>
                                    <FiInfo size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('challenge:pt.validateHint')}
                                </div>

                                {/* 메시지들 - 스크롤 가능한 영역 */}
                                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                                    {validationMsg && (
                                        <div style={{
                                            color: validationStatus === 'error' ? 'var(--color-quiz-warn-text)' :
                                                   validationStatus === 'correct' ? 'var(--color-quiz-success-text)' :
                                                   validationStatus === 'wrong' ? 'var(--color-quiz-fail-text)' : 'var(--color-text-secondary)',
                                            fontSize: 12,
                                            padding: '4px 0',
                                            marginBottom: 4,
                                            lineHeight: 1.4,
                                            wordWrap: 'break-word'
                                        }}>
                                            {validationMsg}
                                        </div>
                                    )}
                                    {wrongDetails && validationStatus === 'wrong' && (
                                        <div style={{
                                            color: 'var(--color-quiz-fail-text)',
                                            fontSize: 11,
                                            padding: '6px',
                                            backgroundColor: 'var(--color-quiz-fail-bg)',
                                            border: '1px solid var(--color-quiz-fail-border)',
                                            borderRadius: 4,
                                            marginBottom: 4
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: 2 }}>{t('challenge:pt.diffAnalysis')}</div>
                                            <div style={{ lineHeight: 1.4, wordWrap: 'break-word' }}>{wrongDetails}</div>
                                        </div>
                                    )}
                                    {submitMsg && (
                                        <div style={{
                                            color: submitMsg.includes('🎉') ? 'var(--color-quiz-success-text)' : submitMsg.includes('❌') ? 'var(--color-quiz-fail-text)' : 'var(--color-quiz-warn-text)',
                                            fontSize: 12,
                                            padding: '4px 0',
                                            fontWeight: 'bold',
                                            lineHeight: 1.4,
                                            wordWrap: 'break-word'
                                        }}>
                                            {submitMsg}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ParticipationTab;